/// <reference types="@angular/localize" />

import { getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import type { Event, EventHint, Integration } from '@sentry/core';

import { AppModule } from './app/app.module';
import { scrubSignedUrlsFromSentryBreadcrumb, scrubSignedUrlsFromSentryEvent, scrubSignedUrlsFromSentrySpan } from './app/shared/util/sentry-scrubbing';
import { environment } from './environments/environment';

type HubSpotWidgetStatus = {
  loaded?: boolean;
};

type HubSpotVisitorIdentification = {
  email: string;
  firstName?: string;
  lastName?: string;
};

type TracitSupportContext = {
  source: 'auth-error';
  type: string;
  title: string;
  message: string;
  attemptedUrl?: string;
  createdAt: string;
};

const handledAgentPermissionDeniedMarker = '__tracitHandledAgentPermissionDenied';

declare global {
  // Firebase App Check reads this global before initialization. Setting it to
  // true prints a debug token that can be registered in the Firebase Console.
  var FIREBASE_APPCHECK_DEBUG_TOKEN: boolean | string | undefined;

  interface Window {
    _hsq?: Array<any>;
    pdfWorkerSrc?: string;
    hsConversationsSettings?: {
      loadImmediately?: boolean;
      identificationEmail?: string;
      identificationToken?: string;
      [key: string]: unknown;
    };
    hsConversationsOnReady?: Array<() => void>;
    HubSpotConversations?: {
      clear?: (options?: { resetWidget?: boolean }) => void;
      on?: (eventName: string, callback: () => void) => void;
      widget?: {
        load?: (options?: { widgetOpen?: boolean }) => void;
        open?: () => void;
        status?: () => HubSpotWidgetStatus;
      };
    };
  }
}

const mobileSupportMediaQuery = '(max-width: 767.98px)';
const supportEmailAddress = 'support@tracit.com.au';
const supportContextTtlMs = 2 * 60 * 1000;

window.pdfWorkerSrc = '/assets/pdfjs/pdf.worker.min.mjs';

if (environment.enableSentry) {
  Sentry.init({
    dsn: 'https://8e9e25264f1695cbfd3dc647bc645e3d@o4511724869320704.ingest.us.sentry.io/4511724913819648',
    beforeSend: (event, hint) => {
      if (isFirestoreUnexpectedStateEvent(event, hint)) {
        return null;
      }

      if (isExpectedAgentPermissionDeniedEvent(event, hint)) {
        return null;
      }

      return scrubSignedUrlsFromSentryEvent(event);
    },
    beforeSendTransaction: (event) => scrubSignedUrlsFromSentryEvent(event),
    beforeSendSpan: (span) => scrubSignedUrlsFromSentrySpan(span),
    beforeBreadcrumb: (breadcrumb) => scrubSignedUrlsFromSentryBreadcrumb(breadcrumb),
    integrations: [
      Sentry.feedbackIntegration({
        id: 'tracit-sentry-feedback',
        autoInject: false,
        colorScheme: "system",
        isNameRequired: true,
        isEmailRequired: true,
        useSentryUser: {
          email: 'email',
          name: 'name',
        },
        triggerLabel: 'Report a bug',
      }),
    ]
  });
}

function isFirestoreUnexpectedStateEvent(event: Event, hint?: EventHint): boolean {
  const values = event.exception?.values ?? [];
  const eventValues = values
    .map(value => value.value)
    .filter((value): value is string => typeof value === 'string');
  const originalExceptionMessages = getErrorMessages(hint?.originalException);

  return [...eventValues, ...originalExceptionMessages].some(isFirestoreUnexpectedStateMessage);
}

function getErrorMessages(error: unknown): string[] {
  if (error instanceof Error) {
    return [error.message, ...getErrorMessages((error as Error & { errors?: unknown }).errors)];
  }

  if (Array.isArray(error)) {
    return error.flatMap(item => getErrorMessages(item));
  }

  return [];
}

function isFirestoreUnexpectedStateMessage(message: string): boolean {
  return message.includes('FIRESTORE')
    && message.includes('INTERNAL ASSERTION FAILED: Unexpected state');
}

function isExpectedAgentPermissionDeniedEvent(event: Event, hint?: EventHint): boolean {
  return hasHandledAgentPermissionDeniedMarker(hint?.originalException)
    || (
      hasPermissionDeniedMessage(event, hint)
      && hasAgentListenerBreadcrumb(event)
    );
}

function hasHandledAgentPermissionDeniedMarker(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  if ((error as Record<string, unknown>)[handledAgentPermissionDeniedMarker] === true) {
    return true;
  }

  return Array.isArray((error as { errors?: unknown }).errors)
    && ((error as { errors?: unknown[] }).errors ?? []).some(hasHandledAgentPermissionDeniedMarker);
}

function hasPermissionDeniedMessage(event: Event, hint?: EventHint): boolean {
  const values = event.exception?.values ?? [];
  const eventValues = values
    .map(value => value.value)
    .filter((value): value is string => typeof value === 'string');
  const originalExceptionMessages = getErrorMessages(hint?.originalException);

  return [...eventValues, ...originalExceptionMessages]
    .some(message => message.includes('Missing or insufficient permissions.'));
}

function hasAgentListenerBreadcrumb(event: Event): boolean {
  return (event.breadcrumbs ?? []).some((breadcrumb) => {
    const message = typeof breadcrumb.message === 'string' ? breadcrumb.message : '';
    const argumentsValue = (breadcrumb.data as { arguments?: unknown } | undefined)?.arguments;
    const argumentMessages = Array.isArray(argumentsValue)
      ? argumentsValue.filter((value): value is string => typeof value === 'string')
      : [];

    return [message, ...argumentMessages].some(value => value.includes('Agent listener error'));
  });
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(mobileSupportMediaQuery).matches;
}

function getSupportContext(): TracitSupportContext | undefined {
  const context = window.tracitSupportContext;

  if (!context?.createdAt) {
    return context;
  }

  const createdAt = Date.parse(context.createdAt);
  const isExpired = Number.isFinite(createdAt) && Date.now() - createdAt > supportContextTtlMs;

  if (isExpired) {
    window.tracitSupportContext = undefined;
    return undefined;
  }

  return context;
}

function toAbsoluteSupportUrl(url?: string): string | undefined {
  if (!url || typeof window === 'undefined') {
    return url;
  }

  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
}

function buildSupportMessage(context?: TracitSupportContext): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return [
      'Hi TRACit support,',
      '',
      'I need help with TRACit.',
      '',
      'Please describe the issue here:',
      '',
    ].join('\n');
  }

  const pageTitle = document.title || 'TRACit';

  if (context?.source === 'auth-error') {
    return [
      'Hi TRACit support,',
      '',
      'I ran into this message in TRACit:',
      '',
      context.title ? `Message: ${context.title}` : undefined,
      context.message ? `Details: ${context.message}` : undefined,
      context.type ? `Error type: ${context.type}` : undefined,
      context.attemptedUrl ? `Page I was trying to open: ${toAbsoluteSupportUrl(context.attemptedUrl)}` : undefined,
      `Current URL: ${window.location.href}`,
      `Title: ${pageTitle}`,
      context.createdAt ? `Time: ${context.createdAt}` : undefined,
      '',
      'Please describe what you were trying to do:',
      '',
    ].filter((line): line is string => line !== undefined).join('\n');
  }

  return [
    'Hi TRACit support,',
    '',
    'I need help with this page:',
    '',
    `URL: ${window.location.href}`,
    `Title: ${pageTitle}`,
    '',
    'Please describe the issue here:',
    '',
  ].join('\n');
}

function buildSupportEmailHref(): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return `mailto:${supportEmailAddress}`;
  }

  const context = getSupportContext();
  const subject = context?.source === 'auth-error'
    ? `TRACit Support Request: ${context.title ?? 'Page issue'}`
    : 'TRACit Support Request';
  const body = buildSupportMessage(context);

  return `mailto:${supportEmailAddress}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function setupSupportTools(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.getElementById('support-tools');
  const toggle = document.getElementById('support-toggle');
  const panel = document.getElementById('support-panel');
  const closeButton = document.getElementById('support-close');
  const chatButton = document.getElementById('support-chat');
  const copyErrorButton = document.getElementById('support-copy-error');
  const copyErrorLabel = document.getElementById('support-copy-error-label');
  const bugButton = document.getElementById('support-bug');
  const emailButton = document.getElementById('support-email');
  const phoneButton = document.getElementById('support-phone');
  const copyrightYear = document.getElementById('support-copyright-year');

  if (!root || !toggle || !panel || !closeButton || !chatButton || !copyErrorButton || !copyErrorLabel || !bugButton || !emailButton || !phoneButton || !copyrightYear) {
    return;
  }

  let isOpen = false;
  let lastScrollY = typeof window !== 'undefined' ? window.scrollY : 0;
  type FeedbackForm = { appendToDom: () => void; open: () => void; el?: HTMLElement };

  let feedbackFormPromise: Promise<FeedbackForm> | null = null;
  let pendingHubSpotOpen = false;
  let hubSpotBindingsApplied = false;
  let currentHubSpotIdentityKey = 'anonymous';
  let currentHubSpotSyncPromise: Promise<void> = Promise.resolve();
  let copyResetTimeout: number | undefined;

  const auth = getAuth(getApp());

  const setSupportChatOpen = (isChatOpen: boolean) => {
    document.body.classList.toggle('support-chat-open', isChatOpen);

    if (isChatOpen) {
      root.classList.remove('is-hidden-offscreen');
    }
  };

  const getHubSpotWidget = () => window.HubSpotConversations?.widget;

  const runWhenHubSpotReady = (callback: () => void) => {
    if (getHubSpotWidget()) {
      callback();
      return;
    }

    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(callback);
  };

  const identifyHubSpotVisitor = (visitor?: Partial<HubSpotVisitorIdentification>) => {
    if (!visitor?.email) {
      return;
    }

    const _hsq = window._hsq = window._hsq || [];
    _hsq.push(['identify', {
      email: visitor.email,
      ...(visitor.firstName ? { firstname: visitor.firstName } : {}),
      ...(visitor.lastName ? { lastname: visitor.lastName } : {}),
    }]);
    _hsq.push(['trackPageView']);
  };

  const identifyHubSpotSupportContext = () => {
    const context = getSupportContext();

    if (context?.source !== 'auth-error') {
      return;
    }

    const _hsq = window._hsq = window._hsq || [];
    _hsq.push(['identify', {
      tracit_support_source: context.source,
      tracit_support_error_type: context.type,
      tracit_support_error_title: context.title,
      tracit_support_error_message: context.message,
      tracit_support_attempted_url: toAbsoluteSupportUrl(context.attemptedUrl),
      tracit_support_created_at: context.createdAt,
    }]);
    _hsq.push(['trackPageView']);
  };

  const bindHubSpotWidgetEvents = () => {
    if (hubSpotBindingsApplied || !window.HubSpotConversations?.on) {
      return;
    }

    const eventBindings: Record<string, boolean> = {
      widgetOpen: true,
      widgetOpened: true,
      widgetClose: false,
      widgetClosed: false,
      conversationStarted: true,
      conversationClosed: false,
    };

    Object.entries(eventBindings).forEach(([eventName, isChatOpen]) => {
      try {
        window.HubSpotConversations?.on?.(eventName, () => setSupportChatOpen(isChatOpen));
      } catch {
        // Ignore unsupported HubSpot events. We only need one working close/open signal.
      }
    });

    hubSpotBindingsApplied = true;
  };

  const applyHubSpotSettings = (visitor?: HubSpotVisitorIdentification) => {
    window.hsConversationsSettings = {
      ...(window.hsConversationsSettings ?? {}),
      loadImmediately: false,
    };

    identifyHubSpotVisitor(visitor);
  };

  const reloadHubSpotWidget = () => {
    runWhenHubSpotReady(() => {
      bindHubSpotWidgetEvents();

      const widget = getHubSpotWidget();
      if (!widget) {
        return;
      }

      if (widget.status?.().loaded) {
        window.HubSpotConversations?.clear?.({ resetWidget: true });
      }

      widget.load?.({ widgetOpen: pendingHubSpotOpen });
      if (pendingHubSpotOpen) {
        setSupportChatOpen(true);
      }
      pendingHubSpotOpen = false;
    });
  };

  const syncHubSpotVisitor = (user: FirebaseUser | null): Promise<void> => {
    const email = user?.email?.trim().toLowerCase() || '';
    const displayName = (user?.displayName ?? '').trim();
    const visitorKey = user ? `${user.uid}:${email}` : 'anonymous';

    if (currentHubSpotIdentityKey === visitorKey && !pendingHubSpotOpen) {
      return Promise.resolve();
    }

    currentHubSpotIdentityKey = visitorKey;

    applyHubSpotSettings(email ? {
      email,
      ...(displayName ? { firstName: displayName } : {}),
    } : undefined);

    reloadHubSpotWidget();
    return Promise.resolve();
  };

  const syncVisibility = () => {
    root.hidden = false;

    if (!isMobileViewport()) {
      root.classList.remove('is-hidden-offscreen');
    }
  };

  const syncSupportContextActions = () => {
    const hasContext = getSupportContext()?.source === 'auth-error';
    copyErrorButton.hidden = !hasContext;

    if (!hasContext) {
      copyErrorLabel.textContent = 'Copy error details';
    }
  };

  const closePanel = () => {
    isOpen = false;
    root.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-expanded', 'false');
  };

  const openPanel = () => {
    syncSupportContextActions();
    isOpen = true;
    root.classList.add('is-open');
    root.classList.remove('is-hidden-offscreen');
    panel.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-expanded', 'true');
  };

  const copySupportContext = async () => {
    const context = getSupportContext();

    if (context?.source !== 'auth-error') {
      syncSupportContextActions();
      return;
    }

    const message = buildSupportMessage(context);

    try {
      await navigator.clipboard.writeText(message);
      copyErrorLabel.textContent = 'Copied';
    } catch {
      copyErrorLabel.textContent = 'Copy failed';
    }

    if (copyResetTimeout) {
      window.clearTimeout(copyResetTimeout);
    }

    copyResetTimeout = window.setTimeout(() => {
      copyErrorLabel.textContent = 'Copy error details';
    }, 1800);
  };

  const openHubSpotChat = () => {
    identifyHubSpotSupportContext();
    pendingHubSpotOpen = true;
    root.classList.remove('is-hidden-offscreen');
    currentHubSpotSyncPromise.finally(() => {
      runWhenHubSpotReady(() => {
        bindHubSpotWidgetEvents();

        const widget = getHubSpotWidget();
        if (!widget) {
          return;
        }

        if (widget.status?.().loaded) {
          widget.open?.();
        } else {
          widget.load?.({ widgetOpen: true });
        }

        setSupportChatOpen(true);
        pendingHubSpotOpen = false;
      });
    });
    closePanel();
  };

  const prefillSentryFeedbackMessage = (form: FeedbackForm, message: string) => {
    const applyMessage = () => {
      const roots: ParentNode[] = [document];
      const host = document.getElementById('tracit-sentry-feedback');

      if (form.el) {
        roots.unshift(form.el);
        if (form.el.shadowRoot) {
          roots.unshift(form.el.shadowRoot);
        }
      }

      if (host?.shadowRoot) {
        roots.unshift(host.shadowRoot);
      }

      const textarea = roots
        .map(root => root.querySelector<HTMLTextAreaElement>('textarea[name="message"], textarea#message'))
        .find(Boolean);

      if (!textarea || textarea.value) {
        return;
      }

      textarea.value = message;
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
      textarea.dispatchEvent(new Event('change', { bubbles: true }));
    };

    applyMessage();
    window.setTimeout(applyMessage, 100);
    window.setTimeout(applyMessage, 300);
  };

  const openSentryFeedback = () => {
    const context = getSupportContext();
    const supportMessage = buildSupportMessage(context);
    const feedbackIntegration = Sentry.getClient()?.getIntegrationByName<{
      name: string;
      createForm: (options?: {
        formTitle?: string;
        messagePlaceholder?: string;
        tags?: Record<string, string>;
      }) => Promise<FeedbackForm>;
    } & Integration>('Feedback');

    if (!feedbackIntegration?.createForm) {
      return;
    }

    const formOptions = context?.source === 'auth-error'
      ? {
          formTitle: context.title ?? 'Report a page issue',
          messagePlaceholder: supportMessage,
          tags: {
            support_source: context.source,
            support_error_type: context.type ?? 'unknown',
          },
        }
      : undefined;

    feedbackFormPromise = formOptions
      ? feedbackIntegration.createForm(formOptions)
      : feedbackFormPromise ?? feedbackIntegration.createForm();
    feedbackFormPromise.then((form) => {
      form.appendToDom();
      form.open();
      prefillSentryFeedbackMessage(form, supportMessage);
    });
    closePanel();
  };

  const syncSupportEmailLink = () => {
    emailButton.setAttribute('href', buildSupportEmailHref());
  };

  const syncSupportLegalContent = () => {
    copyrightYear.textContent = `${new Date().getFullYear()}`;
  };

  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isOpen) {
      closePanel();
      return;
    }

    openPanel();
  });

  closeButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    closePanel();
  });

  panel.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  root.addEventListener('click', (event) => {
    if (event.target === root) {
      closePanel();
    }
  });

  chatButton.addEventListener('click', openHubSpotChat);
  copyErrorButton.addEventListener('click', copySupportContext);
  bugButton.addEventListener('click', openSentryFeedback);
  emailButton.addEventListener('mouseenter', syncSupportEmailLink);
  emailButton.addEventListener('focus', syncSupportEmailLink);
  emailButton.addEventListener('click', () => {
    syncSupportEmailLink();
    closePanel();
  });
  phoneButton.addEventListener('click', () => closePanel());

  window.addEventListener('scroll', () => {
    if (!isMobileViewport() || isOpen || document.body.classList.contains('support-chat-open')) {
      return;
    }

    const currentScrollY = window.scrollY;
    const isNearTop = currentScrollY < 72;
    const isScrollingDown = currentScrollY > lastScrollY + 12;
    const isScrollingUp = currentScrollY < lastScrollY - 12;

    if (isNearTop || isScrollingUp) {
      root.classList.remove('is-hidden-offscreen');
    } else if (isScrollingDown) {
      root.classList.add('is-hidden-offscreen');
    }

    lastScrollY = currentScrollY;
  }, { passive: true });

  document.addEventListener('click', (event) => {
    if (!isOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (target && root.contains(target)) {
      return;
    }

    closePanel();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen) {
      closePanel();
    }
  });

  window.addEventListener('resize', syncVisibility);
  onAuthStateChanged(auth, (user) => {
    currentHubSpotSyncPromise = syncHubSpotVisitor(user);
  });
  window.addEventListener('load', bindHubSpotWidgetEvents, { once: true });
  window.setTimeout(bindHubSpotWidgetEvents, 1200);
  window.setTimeout(bindHubSpotWidgetEvents, 3500);
  syncSupportLegalContent();
  syncSupportEmailLink();
  syncVisibility();
  bindHubSpotWidgetEvents();
}

enableLocalAppCheckDebugToken();

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .then((moduleRef) => {
    if (environment.enableSentry) {
      moduleRef.injector.get(Sentry.TraceService);
    }

    setupSupportTools();
  })
  .catch(err => console.error(err));

function enableLocalAppCheckDebugToken(): void {
  if (!environment.enableAppCheckDebugToken || typeof window === 'undefined') {
    return;
  }

  globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN ??= true;
  console.info('Firebase App Check debug mode is enabled for this build.');
}
